import { Button, RadioChangeEvent, Space } from "antd";
import { Fragment, useEffect, useState } from "react";
import {
  Link,
  useLocation,
  useNavigate,
  useParams,
  useSearchParams,
} from "react-router-dom";
import { useAppSelector } from "../../../app/store";
import BackButtonIcon from "../../../assets/icons/BackButtonIcon";
import UserImg from "../../../assets/images/avatar.svg";
import InfoItem from "../../../components/common/InfoItem";
import TopCard from "../../../components/common/TopCard";
import AdditionalInformation from "../../../components/Employees/view/AdditionalInformation";
import EmergencyInfo from "../../../components/Employees/view/EmergencyInfo";
import FinancialInfo from "../../../components/Employees/view/FinancialInfo";
import InvitedEmployeeActive from "../../../components/Employees/view/InvitedEmployeeActive";
import OfficialInfo from "../../../components/Employees/view/OfficialInfo";
import PersonalInfo from "../../../components/Employees/view/PersonalInfo";
import SalaryInfo from "../../../components/Employees/view/SalaryInfo";
import ConfirmModal from "../../../components/ui/ConfirmModal";
import CustomEmpty from "../../../components/ui/CustomEmpty";
import Loader from "../../../components/ui/Loader";
import {
  useDeleteInvitedMutation,
  useGetEmployeeDetailsQuery,
  useLazyDownloadEmployeeDetailsQuery,
  useReInvitedMutation,
  useUpdateEmployeeStatusMutation,
} from "../../../features/employee/employeeApi";
import { useGetAdditionalDataQuery } from "../../../features/employeeAdditionalField/employeeAdditionalFieldApi";
import { useGetEmployeeBreakdownSalaryDetailsQuery } from "../../../features/employeeSalarySetting/employeeSalarySettingApi";
import {
  useGetPayComponentSettingQuery,
  useGetPayrollSettingQuery,
} from "../../../features/payrollSetting/payrollSettingApi";
import useError from "../../../hooks/useError";
import useModal from "../../../hooks/useModal";
import SidebarLayout from "../../../layouts/SidebarLayout";
import { formatDate, formatTime } from "../../../lib/time";
import { toast } from "../../../lib/toast";
import { EmployeeStatusType } from "../../../types";
import { AdditionDeductionType } from "../../../types/payroll";
import { capitalize } from "../../../utils/string";
import { backLink, underscoreToCapitalizeString } from "../../../utils/utils";
import NotFound from "../../NotFound";
import { typeListCalculationSelect } from "../Settings/Payroll/AdditionsModal";

const statusList: EmployeeStatusType[] = ["active", "inactive"];

export const employeeDetailsList = [
  "Official",
  "Personal",
  "Financial",
  "Salary",
  "Emergency",
  // "Additional Information",
  // "Activity Log",
] as const;

export const employeeDetailsListForEdit = [
  "Official",
  // "Personal",
  // "Financial",
  "Salary",
  // "Emergency",
  // "Additional Information",
  // "Activity Log",
] as const;

type ItemType = (typeof employeeDetailsList)[number];
type ModalType =
  | "CONFIRM"
  | "SEND_INVITATION_AGAIN"
  | "DELETE_INVITE"
  | "ACTIVE_INVITED_EMPLOYEE";

export const useEmployeeProfile = (viewId: string) => {
  let [queryParams, setSearchParams] = useSearchParams();
  const [item, setItem] = useState<ItemType | number>("Official");
  const {
    isLoading: isAdditionalLoading,
    isError: isAdditionalError,
    error: additionalError,
    data: additionalData,
  } = useGetAdditionalDataQuery({});
  const { isError, error, data, isLoading } = useGetEmployeeDetailsQuery(
    viewId || "",
    {
      skip: !viewId,
    }
  );
  const { isLoading: isSalaryLoading, data: salaryData } =
    useGetEmployeeBreakdownSalaryDetailsQuery(viewId || "", {
      skip: !viewId || item !== "Salary",
    });
  const {
    isLoading: isComponentSettingLoading,
    isError: isComponentSettingError,
    error: componentSettingError,
    data: componentSettingData,
  } = useGetPayComponentSettingQuery(
    { params: { business_member_id: Number(viewId) } },
    {
      skip: !viewId || item !== "Salary",
    }
  );

  const componentDetails = componentSettingData?.data?.pay_components;
  useError(isAdditionalError, additionalError, "Get additional data fail");
  useError(isError, error, "Get employee data fail");
  useError(
    isComponentSettingError,
    componentSettingError,
    "Get additions and deductions data fail"
  );

  const employeeDetails = data?.data?.employee;

  function getInfoData() {
    if (item === "Official") {
      return <OfficialInfo data={data} />;
    } else if (item === "Personal") {
      return <PersonalInfo data={data} />;
    } else if (item === "Financial") {
      return <FinancialInfo data={data} />;
    } else if (item === "Salary") {
      return (
        <SalaryInfo
          showEditButton={data?.data?.employee?.status === "invited"}
        />
      );
    } else if (item === "Emergency") {
      return <EmergencyInfo data={data} />;
    } else if (checkAddition(item)) {
      return <AdditionalInformation data={data} />;
    } else {
      return undefined;
    }

    function checkAddition(value: ItemType | number) {
      const itemData = Number(value);
      return !isNaN(itemData);
    }
  }

  useEffect(() => {
    if (queryParams.get("type")) {
      setItem(queryParams.get("type") as ItemType);
    }
  }, [queryParams]);

  return {
    error,
    isLoading,
    employeeDetails,
    employeeDetailsList,
    setItem,
    queryParams,
    setSearchParams,
    item,
    isAdditionalLoading,
    additionalData,
    getInfoData,
    isComponentSettingLoading,
    componentDetails,
    isSalaryLoading,
    salaryData,
    data,
  };
};

export default function EmployeesDetails() {
  const { viewId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { authInfo } = useAppSelector((state) => state.auth);
  const {
    error,
    isLoading,
    employeeDetails,
    employeeDetailsList,
    setItem,
    setSearchParams,
    item,
    isAdditionalLoading,
    additionalData,
    getInfoData,
    isComponentSettingLoading,
    componentDetails,
    isSalaryLoading,
    salaryData,
    data,
  } = useEmployeeProfile(String(viewId));
  const [status, setStatus] = useState<EmployeeStatusType | undefined>(
    undefined
  );
  const { open, handleOpen, handleClose } = useModal<undefined, ModalType>();
  const [
    updateEmployeeStatus,
    {
      isLoading: isUpdateLoading,
      isError: isUpdateStatusError,
      error: updateStatusError,
      isSuccess: isUpdateStatusSuccess,
    },
  ] = useUpdateEmployeeStatusMutation();
  const [
    deleteInvited,
    {
      isError: isDeleteError,
      error: deleteError,
      isSuccess: isDeleteSuccess,
      isLoading: isDeleteLoading,
    },
  ] = useDeleteInvitedMutation();
  const [
    reInvited,
    {
      isError: isReInviteError,
      error: reInviteError,
      isSuccess: isReInviteSuccess,
      isLoading: isReInviteLoading,
    },
  ] = useReInvitedMutation();

  const [downloadEmployeeDetails, { isLoading: isDownloading, data: pdfUrl }] =
    useLazyDownloadEmployeeDetailsQuery();
  useError(
    isUpdateStatusError,
    updateStatusError,
    "Employee update status fail"
  );
  useError(isDeleteError, deleteError, "Employee delete fail");
  useError(isReInviteError, reInviteError, "Re-invitation mail sent fail");

  const handleStatus = (e: RadioChangeEvent) => {
    if (employeeDetails?.status !== e.target.value) {
      handleOpen({ type: "CONFIRM", data: e.target.value });
    }
  };

  const handleConfirm = (val?: EmployeeStatusType) => {
    if (viewId && val)
      updateEmployeeStatus({
        body: {
          status: val,
        },
        id: viewId,
      });
  };

  const handleSandInvitationAgain = (): void => {
    reInvited({
      id: Number(viewId),
      body: {
        email: employeeDetails?.member?.profile?.email,
        profile_id: employeeDetails?.member?.profile?.id,
      },
    });
  };

  const handleDownloadEmployeeDetails = () => {
    downloadEmployeeDetails({ id: data?.data?.employee?.id });
  };

  useEffect(() => {
    if (pdfUrl) {
      const link = document.createElement("a");
      link.href = pdfUrl;
      link.download = "co_worker_details.pdf";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  }, [pdfUrl]);

  useEffect(() => {
    if (employeeDetails?.status) {
      setStatus(employeeDetails?.status);
    }
  }, [employeeDetails?.status]);

  useEffect(() => {
    if (isUpdateStatusSuccess) {
      toast("success", "Status updated successfully");
      handleClose();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isUpdateStatusSuccess]);

  useEffect(() => {
    if (isReInviteSuccess) {
      toast("success", "Re-invitation mail sent successfully");
      handleClose();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isReInviteSuccess]);

  useEffect(() => {
    if (isDeleteSuccess) {
      toast("success", "Employee delete successfully");
      handleClose();
      navigate(`/admin/employees${location.search}`);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isDeleteSuccess]);

  if (error && "status" in error && error.status === 404) {
    return <NotFound title="Sorry, data not found" />;
  }

  return (
    <SidebarLayout title="Employee Details">
      {open?.type === "CONFIRM" && (
        <ConfirmModal
          open={open?.open}
          handleOk={() => handleConfirm(open?.data)}
          handleClose={handleClose}
          okText="Confirm"
          confirmTitle={`${capitalize(open?.data || "")} User.`}
          questionText={`Are sure to ${open?.data} this user?`}
          okLoading={isUpdateLoading}
        />
      )}

      {open?.type === "SEND_INVITATION_AGAIN" && (
        <ConfirmModal
          open={open.open}
          handleClose={handleClose}
          handleOk={handleSandInvitationAgain}
          confirmTitle="Confirmation"
          questionText="Are you sure you want send invitation mail again?"
          okText="Send"
          okLoading={isReInviteLoading}
        />
      )}
      {open?.type === "DELETE_INVITE" && (
        <ConfirmModal
          open={open.open}
          handleClose={handleClose}
          handleOk={() => deleteInvited(Number(viewId))}
          confirmTitle="Confirmation"
          questionText="Are you sure you want to delete?"
          okText="Yes"
          cancelText="No"
          okLoading={isDeleteLoading}
        />
      )}

      {open?.type === "ACTIVE_INVITED_EMPLOYEE" && (
        <InvitedEmployeeActive open={open?.open} handleClose={handleClose} />
      )}

      {isLoading ? (
        <Loader />
      ) : (
        <>
          <TopCard
            size={16}
            align="center"
            leftChildren={
              <Link to={`${backLink(location.pathname, 2)}${location.search}`}>
                <Button
                  className="normal-dark-button"
                  icon={<BackButtonIcon />}
                >
                  Back
                </Button>
              </Link>
            }
          >
            {/* <div className="flex items-center gap-4">
                <div className="w-14 h-14">
                  <img
                    className="w-full h-full rounded-full"
                    src={employeeDetails?.member?.profile?.pro_pic || UserImg}
                    alt=""
                  />
                </div>
                <div className="flex flex-col gap-0.5">
                  <h3 className="text-body text-lg font-semibold leading-normal">
                    {employeeDetails?.member?.profile?.name}
                  </h3>

                  <p className="text-body-text text-sm font-medium leading-normal">
                    {employeeDetails?.role?.name}
                  </p>
                </div>
              </div> */}
            {authInfo?.role === "Admin" && (
              <>
                {employeeDetails?.status === "invited" ? (
                  <Space size={16}>
                    {/* <Button
                      className="download-button"
                      onClick={() =>
                        handleOpen({ type: "ACTIVE_INVITED_EMPLOYEE" })
                      }
                    >
                      Activate
                    </Button>
                    <Button
                      type="primary"
                      className="!bg-[#001652] sm-button !px-3"
                      onClick={() =>
                        handleOpen({ type: "SEND_INVITATION_AGAIN" })
                      }
                    >
                      Invite again
                    </Button>
                    <Button
                      className="delete-button"
                      onClick={() => handleOpen({ type: "DELETE_INVITE" })}
                    >
                      Delete
                    </Button> */}
                  </Space>
                ) : (
                  <>
                    {/* <Radio.Group
                      value={status}
                      className="custom-radio success-radio"
                      size="large"
                      onChange={handleStatus}
                    >
                      {statusList?.map((el, idx) => (
                        <Radio
                          className="text-header text-base font-medium leading-7 mr-3.5 cursor-pointer"
                          value={el}
                          key={idx}
                        >
                          {capitalize(el)}
                        </Radio>
                      ))}
                    </Radio.Group>
                    <Button
                      loading={isDownloading}
                      className="middle-button normal-dark-button"
                      icon={<PrintIcon />}
                      // onClick={downloadPDF}
                      onClick={handleDownloadEmployeeDetails}
                    /> */}
                  </>
                )}
              </>
            )}

            {/* <Button
              type="primary"
              className="middle-button"
              icon={<SettingIcon />}
            /> */}
          </TopCard>

          <div className="bg-white rounded-md grid md:grid-cols-4">
            <ul className="p-4 border-r border-extra-light">
              <li className="flex items-center gap-4 mb-4">
                <div className="w-14 h-14">
                  <img
                    className="w-full h-full rounded-full object-cover"
                    src={employeeDetails?.member?.profile?.pro_pic || UserImg}
                    alt=""
                  />
                </div>
                <div className="flex flex-col gap-0.5">
                  <h3 className="text-body text-lg font-semibold leading-normal">
                    {employeeDetails?.member?.profile?.name}
                  </h3>

                  <p className="text-body-text text-sm font-medium leading-normal">
                    {employeeDetails?.role?.name}
                  </p>
                </div>
              </li>

              {employeeDetailsList?.map((el) => (
                <li
                  key={el}
                  onClick={() => {
                    setItem(el);
                    setSearchParams({ type: el });
                  }}
                  className={`font-medium h-10 px-3 rounded-lg flex items-center cursor-pointer ${
                    el === item ? "bg-[#fff1eb]/50 text-primary" : "text-muted"
                  }`}
                >
                  {el}
                </li>
              ))}
              {isAdditionalLoading ? (
                <li className="text-center py-1 text-body">Loading...</li>
              ) : (
                additionalData?.data?.additionalSections?.map((el, index) => (
                  <li
                    key={el?.id}
                    onClick={() => {
                      setItem(el?.id);
                      setSearchParams({ type: el?.id?.toString() });
                    }}
                    className={`font-medium h-10 px-3 rounded-lg flex items-center cursor-pointer ${
                      item?.toString() === el?.id?.toString()
                        ? "bg-[#fff1eb]/50 text-primary"
                        : "text-muted"
                    }`}
                  >
                    {el?.label}
                  </li>
                ))
              )}
            </ul>
            <div className="md:col-span-3 p-4">{getInfoData()}</div>
          </div>
          {item !== "Salary" ? null : isComponentSettingLoading ? (
            <div className="py-4">
              <Loader />
            </div>
          ) : !componentDetails?.addition?.length &&
            !componentDetails?.deduction?.length ? null : (
            <div className="bg-white rounded-md mt-6 p-4">
              <div className="grid sm:grid-cols-2 gap-10">
                <div>
                  <h5 className="text-header font-semibold text-xl mb-3">
                    Addition Details
                  </h5>
                  {componentDetails?.addition?.length ? (
                    componentDetails?.addition?.map((item, idx) => (
                      <ComponentDetails
                        item={item}
                        index={idx}
                        key={item?.id}
                      />
                    ))
                  ) : (
                    <div>
                      <CustomEmpty rootClassName="py-20" />
                    </div>
                  )}
                </div>
                <div>
                  <h5 className="text-header font-semibold text-xl mb-3">
                    Deduction Details
                  </h5>
                  {componentDetails?.deduction?.length ? (
                    componentDetails?.deduction?.map((item, idx) => (
                      <ComponentDetails
                        item={item}
                        index={idx}
                        key={item?.id}
                      />
                    ))
                  ) : (
                    <div>
                      <CustomEmpty rootClassName="py-20" />
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {item !== "Salary" ? null : isSalaryLoading ? (
            <div className="py-4">
              <Loader />
            </div>
          ) : salaryData?.data?.salary_info?.gross_salary_log?.length ? (
            <div className="bg-white rounded-md mt-6 p-4 pb-0">
              <h5 className="text-header font-semibold text-xl mb-3">
                Salary Logs
              </h5>
              <ul>
                {salaryData?.data?.salary_info?.gross_salary_log?.map((log) => (
                  <InfoItem
                    title={`${formatTime(log?.created_at)} - ${formatDate(
                      log?.created_at
                    )}`}
                    key={log?.id}
                  >
                    {log?.log}
                  </InfoItem>
                ))}
              </ul>
            </div>
          ) : null}
        </>
      )}

      {/* <PrintableContent ref={currentRef}>
        <EmployeeDetailsPDF />
      </PrintableContent> */}
    </SidebarLayout>
  );
}

export const ComponentDetails = ({
  item,
  index,
}: {
  item: AdditionDeductionType;
  index: number;
}) => {
  const [sData, setSData] = useState<any>();
  const { data: salaryData, isError, error } = useGetPayrollSettingQuery();
  useError(isError, error, "Get salary breakdown data fail");

  useEffect(() => {
    if (salaryData) {
      setSData([
        ...(typeListCalculationSelect || []),
        ...(salaryData?.data?.payroll_setting?.salary_breakdown
          ?.filter((item) => item?.is_active)
          ?.map((item) => ({
            label: `% of ${item?.title}`,
            value: item?.id?.toString(),
          })) || []),
      ]);
    }
  }, [salaryData]);

  const componentType = (on_what: string) =>
    sData?.find((el: any) => el?.value === on_what).label;

  return (
    <div className="mb-5 last:mb-0">
      <h4 className="text-header font-semibold text-sm">Item {index + 1}:</h4>
      <ul className="ml-5">
        <InfoItem title="Component Title">{item?.value}</InfoItem>
        {item?.packages?.map((pkg, pkgIndex) => (
          <Fragment key={pkg?.id}>
            <li>
              <h4 className="text-header font-semibold text-xs pt-2">
                #{pkgIndex + 1}
              </h4>
            </li>
            <InfoItem rootClassName="pt-1" title="Package Name">
              {pkg?.name}
            </InfoItem>
            <InfoItem rootClassName="pt-1" title="Component Type">
              {componentType(pkg?.on_what)}
            </InfoItem>
            <InfoItem rootClassName="pt-1" title="Component Value">
              {`${
                pkg?.on_what === "fixed_amount"
                  ? pkg?.amount
                  : `${Number(pkg?.amount)}%`
              }`}
            </InfoItem>
            <InfoItem rootClassName="pt-1" title="Come from">
              {underscoreToCapitalizeString(pkg?.effective_for)}
            </InfoItem>
          </Fragment>
        ))}
      </ul>
    </div>
  );
};
